import React, { Fragment, useEffect, useReducer } from 'react';
import { Row, Col, Form, Button, Container, FormGroup, Table } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { Currency } from '../lib/components/Currency';
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from '../lib/components/LoadingSpinner';
import { ContractLayout } from './ContractLayout';
import { withHeaderMessages } from '../higherOrderComponents/WithHeaderMessages';
import qs from 'qs';

const CombinedContractDisbursementQuoteRequest = (props) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { n } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const initialState = {
    isLoading: true,
    isError: false,
    disbursementsLoading: true,
    disbursements: [],
    disclosureAgreementSelected: false,
    contract: {
      provider: {
        address: {},
      },
      purchaser: {
        address: {},
      },
      recipients: [],
    },
    contracts: [],
    contractsQueryString: n,
    contractNumbers: n.split(",") || [],
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case 'contracts':
        return {
          ...state,
          contracts: action.payload,
          isLoading: false,
        };
      case 'disbursements':
        return {
          ...state,
          disbursements: action.payload,
          disbursementsLoading: false,
        };
      case 'disbursementsLoading':
        return {
          ...state,
          disbursementsLoading: action.payload,
        };
      case 'disclosureAgreementSelected':
        return {
          ...state,
          disclosureAgreementSelected: action.payload,
        };
      case 'isLoading':
        return {
          ...state,
          isLoading: action.payload,
        };
      case 'hideLoader':
        return {
          ...state,
          isLoading: false,
        };
      case 'error':
        return {
          ...state,
          isError: action.payload,
          isLoading: false,
        };
      default:
        throw new Error();
    }
  };

  const [combinedContractState, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const { contractNumbers } = combinedContractState;
    props.header.setHeader("combinedContractQuote");

    Promise.all(
      contractNumbers.map((n) =>
        props.api.get(`/api/contract/${n}`).then((response) => {
          if (response.status === 200) {
            return response.data;
          }
        })
      )
    )
      .catch(() => dispatch({ type: 'isLoading', payload: false }))
      .then((contracts) => {
        dispatch({ type: 'contracts', payload: contracts });

        Promise.all(
          contracts
            .filter((c) => c.status === 'Accepted')
            .map((c) =>
              props.api
                .getContractDisbursementDefaults(
                  `/api/contract/${c.number}/disbursement-defaults`
                )
                .then((response) => {
                  if (response.status === 200) {
                    return {
                      disbursement: response.data,
                      purchases: response.data.purchases.map((p) => {
                        return { selected: false, purchase: p };
                      }),
                    };
                  }
                })
            )
        )
          .catch(() => {
            dispatch({ type: 'disbursementsLoading', payload: false });
            dispatch({ type: 'error', payload: true });
          })
          .then((disbursements) => {
            dispatch({ type: 'disbursements', payload: disbursements });
          });
      });
  }, []);

  const handleSelectAllPurchases = (event) => {
    const updatedDisbursements = combinedContractState.disbursements.map(
      (d) => {
        const updatedPurchases = d.purchases.map((p) => {
          return { ...p, selected: event.target.checked };
        });
        return { ...d, purchases: updatedPurchases };
      }
    );

    dispatch({ type: 'disbursements', payload: updatedDisbursements });
  };

  const handleSelectPurchase = (purchase, event) => {
    const updatedDisbursements = combinedContractState.disbursements.map(
      (d) => {
        if (!d.purchases.some((p) => p === purchase)) {
          return d;
        } else {
          const updatedPurchases = d.purchases.map((p) => {
            return p.purchase.purchaseId === purchase.purchase.purchaseId
              ? { ...p, selected: event.target.checked }
              : p;
          });
          return { ...d, purchases: updatedPurchases };
        }
      }
    );

    dispatch({ type: 'disbursements', payload: updatedDisbursements });
  };

  const allPurchasesSelected = () => {
    return (
      combinedContractState.disbursements
        .flatMap((d) => d.purchases)
        .filter((p) => !p.selected).length === 0
    );
  };

  const handleDisclosureAgreementSelectedChanged = (event) => {
    dispatch({
      type: 'disclosureAgreementSelected',
      payload: event.target.checked,
    });
  };

  const canQuoteBeRequested = () => {
    return !(
      combinedContractState.disbursements
        .flatMap((d) => d.purchases)
        .filter((p) => p.selected).length > 0 &&
      combinedContractState.disclosureAgreementSelected
    );
  };

  const handleRequestQuoteFormSubmit = (event) => {
    event.preventDefault();
    navigate(`/contracts-disbursement-quote`, {
      state: {
        purchases: combinedContractState.disbursements
          .map((d) => {
            return d.purchases.map((p) => {
              return {
                ...p.purchase,
                contractNumber: d.disbursement.contractNumber,
                selected: p.selected,
              };
            });
          })
          .flatMap((purchase) => purchase)
          .filter((purchase) => purchase.selected),
      },
    });
  };

  const renderContract = (disbursement, i) => {
    const status = combinedContractState.contracts.some(
      (c) => c.status === "Accepted"
    )
      ? "Accepted"
      : "";

    return (
      <ContractLayout
        contractNumber={disbursement.disbursement.contractNumber}
        queryString={combinedContractState.contractsQueryString}
        contractStatus={status}
        isCombined={true}
        displayButtons={i === 0}
        label={"Contract " + disbursement.disbursement.contractNumber}
        key={i}
      >
        <Row>
          <Col sm={12}>
            <Table className="provider-location-table">
              <thead>
                <tr>
                  <th className="col-sm-2 p-0">
                    {i === 0 && (
                      <Fragment>
                        <FormGroup>
                          <Form.Check
                            onChange={handleSelectAllPurchases}
                            checked={allPurchasesSelected()}
                            label={'Include All Items'}
                            style={{ marginRight: "5px" }}
                          />
                        </FormGroup>
                      </Fragment>
                    )}
                  </th>
                  <th>Purchase</th>
                  <th>Recipient(s)</th>
                  <th style={{ textAlign: "right" }}>Plan Amount</th>
                  <th style={{ textAlign: "right" }}>Trusted</th>
                  <th style={{ textAlign: "right" }}>Retained</th>
                  <th style={{ textAlign: "right" }}>Income</th>
                  <th>Guaranteed?</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {disbursement.purchases &&
                  disbursement.purchases.map((p, i) => (
                    <tr key={i}>
                      <td className="text-center">
                        <Form.Check
                          checked={p.selected}
                          onChange={(event) => handleSelectPurchase(p, event)}
                        />
                      </td>
                      <td>
                        {p.purchase.group} - {p.purchase.category} -{" "}
                        {p.purchase.description}
                      </td>
                      <td>
                        {p.purchase.recipients.map((recipient, index) => {
                          return <p key={index}>{recipient}</p>;
                        })}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Currency amount={p.purchase.netPrice} />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Currency amount={p.purchase.totalTrusted} />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Currency amount={p.purchase.totalRetained} />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Currency
                          amount={
                            p.purchase.incomeAmount +
                            (disbursement.disbursement.payAtMarketValue
                              ? p.purchase.totalUnrealizedGainOrLoss
                              : 0)
                          }
                        />
                      </td>
                      <td>{p.purchase.guaranteed ? "Yes" : "No"}</td>
                      <td style={{ textAlign: "right" }}>
                        <Currency
                          amount={
                            p.selected
                              ? p.purchase.principalAmount +
                                p.purchase.incomeAmount +
                                (disbursement.disbursement.payAtMarketValue
                                  ? p.purchase.totalUnrealizedGainOrLoss
                                  : 0)
                              : 0
                          }
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </ContractLayout>
    );
  };

  if (
    combinedContractState.isLoading ||
    combinedContractState.disbursementsLoading
  ) {
    return <LoadingSpinner />;
  }
  if (combinedContractState.isError) {
    return <div></div>;
  }

  const { contracts, disbursements } = combinedContractState;
  const contractNumbersNotIncluded = contracts
    .filter(
      (c) =>
        !disbursements.some((d) => d.disbursement.contractNumber === c.number)
    )
    .map((c) => c.number)
    .join(", ");

  return (
    <Form onSubmit={handleRequestQuoteFormSubmit}>
      {disbursements.map((disbursement, i) => renderContract(disbursement, i))}
      {contractNumbersNotIncluded && (
        <Row>
          <Col>
            <p style={{ marginBottom: "25px", color: "#6C6C6D" }}>
              Some contracts have been excluded because they have already been
              delivered: {contractNumbersNotIncluded}.
            </p>
          </Col>
        </Row>
      )}
      <Container>
        <Col>
          <p style={{ marginBottom: "25px", color: "#6C6C6D" }}>
            Distribution Quote Disclosure: Quoted values are based on all
            activity processed through the previous day; the amount is subject
            to change based on additional trust activity including income,
            investment gains, and fees.
          </p>
        </Col>
      </Container>
      <Container>
        <Col>
          <FormGroup>
            <Form.Check
              checked={combinedContractState.disclosureAgreementSelected}
              onChange={handleDisclosureAgreementSelectedChanged}
              label={`I acknowledge I have read the distribution quote disclosure above and understand that actual payment received
                            may differ from the value quoted above.`}
            />
          </FormGroup>
        </Col>
      </Container>
      <Container>
        <Col className="text-start">
          <Button
            type="submit"
            className="btn btn-primary mb-3"
            disabled={canQuoteBeRequested()}
          >
            REQUEST QUOTE
          </Button>
        </Col>
      </Container>
    </Form>
  );
};

export default withHeaderMessages(
  WithApiAccess(CombinedContractDisbursementQuoteRequest)
);
